import React from 'react';

import { Check as CheckIcon, MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { alpha, Divider, Stack, Typography } from '@mui/material';

import { BLACK, DARK_GREY, LIGHT_TEAL, SCOUT_BLUE, SCOUT_TEAL } from '../../ThemeContext/constants';

export interface WizardStepperProps {
  activeStep: number;
  ariaLabel?: string;
  id?: string;
  lockedSteps?: number[];
  maxStepReached: number;
  onStepClick: (index: number) => void;
  pendingStepIndex?: number;
  steps: string[];
}

const INACTIVE_CIRCLE_FILL = alpha(BLACK, 0.06);
const HOVER_CIRCLE_FILL = alpha(SCOUT_TEAL, 0.35);

const StepGlyph = ({
  index,
  isCompleted,
  isPendingPastStep
}: {
  index: number;
  isCompleted: boolean;
  isPendingPastStep: boolean;
}): React.ReactNode => {
  if (isPendingPastStep) {
    return <MoreHorizIcon aria-hidden sx={{ fontSize: 15 }} />;
  }
  if (isCompleted) {
    return <CheckIcon aria-hidden sx={{ fontSize: 15 }} />;
  }
  return index + 1;
};

const WizardStepper = ({
  activeStep,
  ariaLabel = 'Wizard steps',
  id = 'wizard-stepper',
  lockedSteps,
  maxStepReached,
  onStepClick,
  pendingStepIndex,
  steps
}: WizardStepperProps): React.ReactElement => (
  <Stack
    aria-label={ariaLabel}
    component="nav"
    direction="row"
    id={id}
    sx={{
      alignItems: 'center',
      gap: '8px',
      width: '100%',
      maxWidth: '760px',
      margin: '0 auto',
      py: '8px'
    }}
  >
    {steps.map((step, index) => {
      const isCompleted = index < activeStep;
      const isActive = index === activeStep;
      const isLast = index === steps.length - 1;
      const isClickable =
        index <= maxStepReached && index !== activeStep && !lockedSteps?.includes(index);
      const isPendingPastStep = isCompleted && index === pendingStepIndex;
      const stepId = `${id}-step-${index}`;
      const stepButtonSx = {
        flexShrink: 0,
        border: 'none',
        backgroundColor: 'transparent',
        padding: 0,
        cursor: isClickable ? 'pointer' : 'default',
        borderRadius: '4px',
        '&:hover': isClickable
          ? {
              '& .step-circle': { backgroundColor: HOVER_CIRCLE_FILL },
              '& .step-label': { color: SCOUT_BLUE }
            }
          : undefined,
        '&:focus-visible': isClickable
          ? { outline: `2px solid ${SCOUT_TEAL}`, outlineOffset: '2px' }
          : { outline: 'none' }
      };
      const stepCircleSx = {
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor: isCompleted || isActive ? LIGHT_TEAL : INACTIVE_CIRCLE_FILL,
        color: isCompleted || isActive ? SCOUT_BLUE : DARK_GREY,
        fontFamily: 'Source Sans Pro, sans-serif',
        fontSize: '12px',
        fontWeight: 700,
        transition: 'background-color 120ms ease'
      };

      return (
        <React.Fragment key={stepId}>
          <Stack
            aria-current={isActive ? 'step' : undefined}
            aria-label={isPendingPastStep ? `${step}, pending` : undefined}
            component={isClickable ? 'button' : 'div'}
            direction="row"
            id={stepId}
            onClick={isClickable ? () => onStepClick(index) : undefined}
            type={isClickable ? 'button' : undefined}
            sx={[
              {
                alignItems: 'center',
                gap: '8px'
              },
              ...(Array.isArray(stepButtonSx) ? stepButtonSx : [stepButtonSx])
            ]}
          >
            <Stack
              className="step-circle"
              sx={[
                {
                  alignItems: 'center',
                  justifyContent: 'center'
                },
                ...(Array.isArray(stepCircleSx) ? stepCircleSx : [stepCircleSx])
              ]}
            >
              <StepGlyph
                index={index}
                isCompleted={isCompleted}
                isPendingPastStep={isPendingPastStep}
              />
            </Stack>
            <Typography
              className="step-label"
              sx={{
                fontFamily: 'Source Sans Pro, sans-serif',
                fontSize: '14px',
                fontWeight: isActive ? 700 : 400,
                color: DARK_GREY,
                whiteSpace: 'nowrap',
                transition: 'color 120ms ease'
              }}
            >
              {step}
            </Typography>
          </Stack>
          {isLast ? null : <Divider sx={{ flex: '1 1 0', minWidth: '48px' }} />}
        </React.Fragment>
      );
    })}
  </Stack>
);

export default WizardStepper;
